import React, { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  Typography,
} from '@mui/material';
import { DateRangePicker } from '../../Components/Date/Datepicker';
import { useSnackbar } from '../../Context/SnackbarProvider';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { COLORS } from '../../Styles/Colors';
import apiClient from '../../Services/ApiClient';

/**
 * What to export. "Consumables" = ABATIX + Non-ABATIX stock items (narrowed by
 * the page's ABATIX / Non-ABATIX filter); "Assets" = ABATIX checked-out tools
 * and equipment. Sent to the API as its existing merge types
 * (abatix / nonAbatix / equipment).
 */
type ReportGroup = 'consumables' | 'assets';
type SortBy = 'location' | 'poJob' | 'employee';
type ItemTypeFilter = 'all' | 'abatix' | 'non-abatix';

interface InitialFilters {
  startDate: Date | null;
  endDate: Date | null;
  selectedOvs: string;
  columnSearches: Record<string, string>;
  /** The Transactions page's ABATIX / Non-ABATIX filter. */
  itemType?: ItemTypeFilter;
}

interface Props {
  open: boolean;
  onClose: () => void;
  initialFilters: InitialFilters;
}

const CONSUMABLES_HINT: Record<ItemTypeFilter, string> = {
  all: 'ABATIX and Non-ABATIX items',
  abatix: 'ABATIX items only (matches the page filter)',
  'non-abatix': 'Non-ABATIX items only (matches the page filter)',
};

// Match the Transactions page columns & header names. ABATIX-only fields (P21*)
// and Non-ABATIX-only (IMS Order No.) are included so merged exports cover
// both; missing values render as '-'.
const PDF_COLUMNS = [
  { key: 'transaction_date',    label: 'Date' },
  { key: 'CustomerName',        label: 'Account' },
  { key: 'location_name',       label: 'Location' },
  { key: 'po_job_number',       label: 'PO/Job No.' },
  { key: 'product_code',        label: 'Part No.' },
  { key: 'product_description', label: 'Description' },
  { key: 'quantity',            label: 'QTY' },
  { key: 'uom',                 label: 'UOM' },
  { key: 'unit_price',          label: 'Price' },
  { key: 'ext_price',           label: 'Ext. Price' },
  { key: 'employee_name',       label: 'Employee' },
  { key: 'requester_name',      label: 'Requester' },
  { key: 'bin',                 label: 'Bin' },
  { key: 'category_name',       label: 'Category' },
  { key: 'item_type',           label: 'Type' },
  { key: 'P21OrderNumber',      label: 'P21 Order No.' },
  { key: 'P21InvoiceNumber',    label: 'P21 Invoice No.' },
  { key: 'ims_order_no',        label: 'IMS Order No.' },
];

function cellValue(row: any, key: string): string {
  if (key === 'ext_price') {
    const explicit = row.ext_price ?? row.amount;
    if (explicit != null && explicit !== '') return String(explicit);
    const qty = Number(row.quantity ?? 0);
    const price = Number(row.unit_price ?? 0);
    return (qty * price).toFixed(2);
  }
  if (key === 'bin') {
    const val = row.bin ?? row.bin_name ?? row.bin_location_id;
    if (val == null || val === '') return '-';
    return String(val);
  }
  const val = row[key];
  if (val == null || val === '') return '-';
  return String(val);
}

function buildFilename(ext: string, startDate: Date | null, endDate: Date | null): string {
  const fmt = (d: Date) => d.toISOString().split('T')[0];
  if (startDate && endDate) {
    return `Transactions_Merged_${fmt(startDate)}_to_${fmt(endDate)}.${ext}`;
  }
  return `Transactions_Merged_All_Dates.${ext}`;
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function generateCsv(rows: any[]): string {
  const headers = PDF_COLUMNS.map((c) => `"${c.label}"`).join(',');
  const lines = rows.map((row) =>
    PDF_COLUMNS.map((c) => `"${cellValue(row, c.key).replace(/"/g, '""')}"`).join(','),
  );
  return [headers, ...lines].join('\n');
}

function generatePdf(rows: any[], startDate: Date | null, endDate: Date | null): jsPDF {
  const doc = new jsPDF('l', 'mm', 'a4');
  const fmt = (d: Date) => d.toLocaleDateString();
  const dateRange = startDate && endDate
    ? `${fmt(startDate)} – ${fmt(endDate)}`
    : 'All Dates';

  doc.setFontSize(16);
  doc.text('Transactions — Merged Export', 14, 15);
  doc.setFontSize(10);
  doc.text(`Date Range: ${dateRange}`, 14, 22);
  doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 28);

  const headers = PDF_COLUMNS.map((c) => c.label);
  const body = rows.map((row) => PDF_COLUMNS.map((c) => cellValue(row, c.key)));

  autoTable(doc, {
    head: [headers],
    body,
    startY: 34,
    theme: 'grid',
    styles: { fontSize: 6, cellPadding: 1.5, overflow: 'linebreak' },
    headStyles: { fillColor: [32, 178, 170], textColor: 255, fontStyle: 'bold' },
    margin: { left: 10, right: 10 },
  });

  return doc;
}

export const MergeExportDialog: React.FC<Props> = ({
  open,
  onClose,
  initialFilters,
}) => {
  const { showSnackbar } = useSnackbar();
  const itemType: ItemTypeFilter = initialFilters.itemType ?? 'all';
  // Assets are ABATIX equipment, so they cannot be part of a Non-ABATIX-only export.
  const assetsAvailable = itemType !== 'non-abatix';

  const [groups, setGroups] = useState<Set<ReportGroup>>(new Set<ReportGroup>(['consumables']));
  const [sortBy, setSortBy] = useState<Set<SortBy>>(new Set<SortBy>());
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [loadingCsv, setLoadingCsv] = useState(false);

  // Only reset when the dialog opens — do not re-run on every render,
  // or the user's checkbox and date choices would be reset while they work.
  useEffect(() => {
    if (!open) return;

    const today = new Date();
    const oneMonthAgo = new Date(today);
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
    setStartDate(initialFilters.startDate ?? oneMonthAgo);
    setEndDate(initialFilters.endDate ?? today);
    setGroups(new Set<ReportGroup>(['consumables']));
    setSortBy(new Set<SortBy>());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally sync only on open
  }, [open]);

  const toggleGroup = (group: ReportGroup) => {
    setGroups((prev) => {
      const next = new Set(prev);
      next.has(group) ? next.delete(group) : next.add(group);
      return next;
    });
  };

  const mergeTypes = (): string[] => {
    const types: string[] = [];
    if (groups.has('consumables')) {
      if (itemType !== 'non-abatix') types.push('abatix');
      if (itemType !== 'abatix') types.push('nonAbatix');
    }
    if (groups.has('assets') && assetsAvailable) types.push('equipment');
    return types;
  };

  const toggleSortBy = (col: SortBy) => {
    setSortBy((prev) => {
      const next = new Set(prev);
      next.has(col) ? next.delete(col) : next.add(col);
      return next;
    });
  };

  const noTypeSelected = mergeTypes().length === 0;

  const buildQueryParams = (): URLSearchParams => {
    const params = new URLSearchParams();

    params.set('mergeTypes', mergeTypes().join(','));
    if (itemType !== 'all') params.set('type', itemType);
    if (sortBy.size > 0) params.set('sortBy', Array.from(sortBy).join(','));
    if (startDate) params.set('startDate', startDate.toISOString().split('T')[0]);
    if (endDate) params.set('endDate', endDate.toISOString().split('T')[0]);
    if (initialFilters.selectedOvs) params.set('ovid', initialFilters.selectedOvs);

    Object.entries(initialFilters.columnSearches).forEach(([field, value]) => {
      if (value.trim()) params.set(field, value.trim());
    });

    return params;
  };

  const fetchRows = async (): Promise<any[] | null> => {
    const params = buildQueryParams();
    const res = await apiClient(`/api/transactions/export?${params.toString()}`);
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      showSnackbar(body.error || 'Export failed. Please try again.', 'error');
      return null;
    }
    return res.json();
  };

  const handleExportCsv = async () => {
    setLoadingCsv(true);
    try {
      const rows = await fetchRows();
      if (!rows) return;
      const csv = generateCsv(rows);
      triggerDownload(
        new Blob([csv], { type: 'text/csv;charset=utf-8;' }),
        buildFilename('csv', startDate, endDate),
      );
    } catch {
      showSnackbar('Export failed. Please try again.', 'error');
    } finally {
      setLoadingCsv(false);
    }
  };

  const handleExportPdf = async () => {
    setLoadingPdf(true);
    try {
      const rows = await fetchRows();
      if (!rows) return;
      const doc = generatePdf(rows, startDate, endDate);
      doc.save(buildFilename('pdf', startDate, endDate));
    } catch {
      showSnackbar('Export failed. Please try again.', 'error');
    } finally {
      setLoadingPdf(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Merge &amp; Export</DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          Reporting to Merge:
        </Typography>
        <FormGroup sx={{ gap: 0.5, mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={groups.has('consumables')}
                onChange={() => toggleGroup('consumables')}
                sx={{ color: COLORS.teal, '&.Mui-checked': { color: COLORS.teal } }}
              />
            }
            label={
              <span>
                Consumables
                <Typography component="span" sx={{ display: 'block', fontSize: '0.8rem', color: COLORS.gray }}>
                  {CONSUMABLES_HINT[itemType]}
                </Typography>
              </span>
            }
          />
          <FormControlLabel
            disabled={!assetsAvailable}
            control={
              <Checkbox
                checked={assetsAvailable && groups.has('assets')}
                onChange={() => toggleGroup('assets')}
                sx={{ color: COLORS.teal, '&.Mui-checked': { color: COLORS.teal } }}
              />
            }
            label={
              <span>
                Assets
                <Typography component="span" sx={{ display: 'block', fontSize: '0.8rem', color: COLORS.gray }}>
                  {assetsAvailable
                    ? 'Checked-out tools and equipment'
                    : 'Not available while the page shows Non-ABATIX only'}
                </Typography>
              </span>
            }
          />
        </FormGroup>

        <Divider sx={{ mb: 2 }} />

        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          Merge Reporting By:
        </Typography>
        <FormGroup row sx={{ gap: 1, mb: 2 }}>
          {(['location', 'poJob', 'employee'] as SortBy[]).map((col) => (
            <FormControlLabel
              key={col}
              control={
                <Checkbox
                  checked={sortBy.has(col)}
                  onChange={() => toggleSortBy(col)}
                  sx={{ color: COLORS.teal, '&.Mui-checked': { color: COLORS.teal } }}
                />
              }
              label={col === 'location' ? 'Location' : col === 'poJob' ? 'PO/Job No.' : 'Employee'}
            />
          ))}
        </FormGroup>

        <Divider sx={{ mb: 2 }} />

        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          Select Date Range:
        </Typography>
        <DateRangePicker
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button variant="outlined" onClick={onClose} sx={{ textTransform: 'capitalize' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleExportPdf}
          disabled={loadingPdf || loadingCsv || noTypeSelected}
          sx={{
            textTransform: 'capitalize',
            backgroundColor: COLORS.teal,
            '&:hover': { backgroundColor: COLORS.teal },
          }}
        >
          {loadingPdf ? 'Exporting…' : 'Export PDF'}
        </Button>
        <Button
          variant="contained"
          onClick={handleExportCsv}
          disabled={loadingPdf || loadingCsv || noTypeSelected}
          sx={{
            textTransform: 'capitalize',
            backgroundColor: COLORS.teal,
            '&:hover': { backgroundColor: COLORS.teal },
          }}
        >
          {loadingCsv ? 'Exporting…' : 'Export CSV'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
